/**
 * Created by Sahar.Rehani on 10/9/2016.
 */

import type { MouseEvent } from "react";
import { Component } from "react";

import classNames from "classnames";
import { debounce, isNil } from "lodash";

import { colorsPalettes } from "@similarweb/styles";

import { ComponentsUtils } from "app/components/ComponentsUtils";

import { setColumnWidth } from "../../SWReactTableUtils";
import type { FlexTableColumnConfig, FlexTableOptions, FlexTableRow, FlexTableTableMetadata } from "../types";

import FlexTableColumn from "./FlexTableColumn";
import {
    FixedFlexTable,
    FlexStickyTableHeaderCell,
    FlexTableCell,
    FlexTableHeaderCell,
    RegularFlexTable,
    StickyFlexTable,
} from "./FlexTableStatelessComponents";
import { TableDropdownComponent } from "./TableDropdownComponentInner";

export const CLEAR_HIGHLIGHT_CLICKED_ROW_EVENT = "clearHighlightClickedRowEvent";
export const UPDATE_HIGHLIGHT_CLICKED_ROW_EVENT = "updateHighlightClickedRowEvent";

type UncontrolledPopup = {
    readonly state: {
        readonly isOpen: boolean;
    };
    openPopup(): void;
    closePopup(): void;
};

type UncontrolledDropdown = {
    readonly popupClickRef: UncontrolledPopup;
};

type FlexTableProps = {
    className?: string;

    type: string;
    tableColumns: readonly FlexTableColumnConfig[];
    tableData: readonly FlexTableRow[];
    tableOptions: FlexTableOptions;
    tableMetadata: FlexTableTableMetadata;
    tableHeight?: number;

    disableRowHover?: boolean;
    allRowsSelected?: boolean;
    visibleParents: unknown;
    activeRowIndex?: number;

    getStableKey(rowIndex: number, column: FlexTableColumnConfig, row?: unknown): string;
    showMoreChildRows(rowData: unknown, value: unknown): void;
    toggleChildRows(rowData: unknown, parentKey: string): void;
    toggleRowAppearance?(isHidden: boolean): Promise<VoidFunction>;

    onSort(column: FlexTableColumnConfig): void;
    onItemClick?(e: MouseEvent): void;
    onItemToggleSelection?(e: MouseEvent): void;
    onSelectAllRows?(e: MouseEvent): void;
    onColumnResize?(column: FlexTableColumnConfig, width: number): void;
    onRowHover?(e: MouseEvent): void;
    onMouseTableLeave?(data?: unknown): void;
};

type FlexTableState = {
    isResponsive: boolean;
    scrollArrowsContainerWidth: number;
    tableColumns: readonly FlexTableColumnConfig[];
    clickedRowIndex: number;
    activeRowIndex: number;
};

export class FlexTable extends Component<FlexTableProps, FlexTableState> {
    /**
     * all elements references (refs)
     */
    public tableWrapperEl!: HTMLElement;

    public scrollableEl!: HTMLElement;

    public tableEl!: HTMLElement;

    public headerEl!: HTMLElement;

    public scrollableStickyEl!: HTMLElement;

    public rowDropdownEl!: HTMLElement;

    private enrichedRowDropdown!: UncontrolledDropdown;

    private enrichedRowResizeObserver?: ResizeObserver;

    public activeRowEl!: JQuery;

    public clickedRowEl!: JQuery;

    public selectedRowEl!: JQuery;

    public resizeTimer: any;

    public dragged: any;

    public COL_MIN_WIDTH = 40;

    public isRegularTable: boolean;

    public onPageResizeDelta = 200;

    public passiveEventOptions: any;

    public headerHeight = 0;

    public rowActionsHeight = -1;

    public rowActionBgWidth = 40;

    public rowActionsIndex = -1; // this isn't in the component's `state` - for performance reasons.

    public lastValidRowActionsIndex = -1;

    public floatingActionsRowIndex = -1; // this isn't in the component's `state` - for performance reasons.

    public rowHighlightIndex = -1; // this isn't in the component's `state` - for performance reasons.

    public exposable: any;

    public prevSelectedRowIndex!: number;

    public checkResponsiveState = debounce((trials = 3) => {
        const executeCheck = () => {
            if (this.scrollableEl) {
                const isResponsive = this.scrollableEl.clientWidth < this.scrollableEl.scrollWidth;
                const scrollArrowsContainerWidth = this.scrollableEl.clientWidth;
                if (
                    isResponsive !== this.state.isResponsive ||
                    scrollArrowsContainerWidth !== this.state.scrollArrowsContainerWidth
                ) {
                    this.setState({
                        isResponsive,
                        scrollArrowsContainerWidth,
                    });
                } else {
                    if (trials > 0) {
                        trials--;
                        setTimeout(executeCheck, 100);
                    }
                }
            }
        };
        executeCheck();
    }, 100);

    //*******************
    //  Lifecycle events
    //*******************
    private columnsResizeEvent: Event;

    constructor(props: FlexTableProps) {
        super(props);
        this.passiveEventOptions = ComponentsUtils.getSupportForPassiveEvents() ? { passive: true } : false;
        // checks if position: sticky is supported in the browser & by the current PRO module layout | TODO: @oleg remove jQuery condition when Layout task is done | also: safari should support it, needs to be fixed.

        this.isRegularTable = this.props.type === "regular";
        this.exposable = {
            toggleRowAppearance: this.toggleRowAppearance,
        };
        this.dragged = {};
        this.state = {
            isResponsive: false,
            scrollArrowsContainerWidth: 0,
            tableColumns: this.props.tableColumns,
            clickedRowIndex: -1,
            activeRowIndex: this.props.activeRowIndex ?? -1,
        };
        this.columnsResizeEvent = new Event("swColumnsResize");
    }

    public componentDidMount() {
        const { initialActiveRow, enrichOnLoadRowNumber } = this.props.tableOptions;

        if (Number.isInteger(initialActiveRow)) {
            // @ts-ignore FIXME: strict migration - initialActiveRow narrowed to number by Number.isInteger but TS doesn't carry the narrowing
            this.toggleSelectedRowElement(initialActiveRow + 1);
        }

        if (enrichOnLoadRowNumber) {
            this.setActiveRowElement(enrichOnLoadRowNumber);
            $(this.tableEl).find(`.collapseControlCell.swReactTableCell:nth-child(${enrichOnLoadRowNumber})`).click();
        }

        if (this.props.activeRowIndex) {
            this.setActiveRowElement(this.props.activeRowIndex);
            this.activeRowEl && this.activeRowEl.addClass("active-row");
        }

        window.addEventListener("resize", this.onPageResize, { capture: true });
        ComponentsUtils.ensureRenderCompleted(this.checkResponsiveState);
        if (!this.isRegularTable) {
            this.scrollableEl.addEventListener("scroll", this.onHorizontalScroll, {
                ...this.passiveEventOptions,
                capture: true,
            });
        }

        this.headerHeight = this.headerEl && this.headerEl.offsetHeight;
        if (this.props.tableOptions.highlightClickedRow) {
            $("body")
                .on(CLEAR_HIGHLIGHT_CLICKED_ROW_EVENT, this.clearHighlightClickedRow)
                .on(UPDATE_HIGHLIGHT_CLICKED_ROW_EVENT, this.updateHighlightClickedRow);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.onPageResize, { capture: true });
        if (!this.isRegularTable) {
            this.scrollableEl.removeEventListener("scroll", this.onHorizontalScroll, {
                ...this.passiveEventOptions,
                capture: true,
            });
        }
        $("body").off("mousemove").off("mouseup");
        if (this.props.tableOptions.highlightClickedRow) {
            $("body")
                .off(CLEAR_HIGHLIGHT_CLICKED_ROW_EVENT, this.clearHighlightClickedRow)
                .off(UPDATE_HIGHLIGHT_CLICKED_ROW_EVENT, this.updateHighlightClickedRow);
        }
        this.disconnectEnrichedRowObserver();
    }

    public componentDidUpdate(_: FlexTableProps, prevState: FlexTableState) {
        if (this.props.tableOptions.isSideBarClosed) {
            this.removeSelectedRowElement();
        }

        if (!isNil(this.props.activeRowIndex) && prevState.activeRowIndex !== this.props.activeRowIndex) {
            if (this.activeRowEl) {
                this.activeRowEl.removeClass("active-row");
            }

            this.setActiveRowElement(this.props.activeRowIndex);
            this.activeRowEl && this.activeRowEl.addClass("active-row");
        }

        this.checkResponsiveState();
    }

    //****************
    //  Class methods
    //****************
    public render() {
        const columns = this.getColumns(),
            { type, tableHeight } = this.props,
            tableModeProps = {
                columns,
                scrollArrowsContainerWidth: this.state.scrollArrowsContainerWidth,
                scrollBy: this.scrollBy,
                setRef: this.setRef,
                onMouseEnterHeader: this.onTableWrapperMouseLeave,
            },
            tableComponent = {
                sticky: (
                    <StickyFlexTable
                        {...tableModeProps}
                        aboveHeaderComponents={this.props.tableOptions.aboveHeaderComponents}
                    />
                ),
                regular: <RegularFlexTable {...tableModeProps} />,
                fixed: (
                    <FixedFlexTable
                        {...tableModeProps}
                        aboveHeaderComponents={this.props.tableOptions.aboveHeaderComponents}
                        tableHeight={tableHeight}
                    />
                ),
            };

        return (
            <div
                className={this.getTableClasses()}
                onMouseLeave={this.onTableWrapperMouseLeave}
                ref={(el) => {
                    this.setRef("tableWrapperEl", el);
                }}
            >
                {tableComponent[type as keyof typeof tableComponent]}
                <TableDropdownComponent
                    tableOptions={this.props.tableOptions}
                    setRef={this.setRef}
                    tableData={this.props.tableData}
                    clickedRowIndex={this.state.clickedRowIndex}
                    onDropdownToggle={this.onDropdownToggle}
                    onDropdownClose={this.collapseEnrichedRowAndCloseDropdown}
                />
                {this.getRowActionsMarkup(false)}
                {this.getRowActionsMarkup(true)}
            </div>
        );
    }

    public setRef = (elName: string, el: FixmeType) => {
        // @ts-ignore FIXME: strict migration - dynamic class property assignment via string key
        this[elName] = el;
    };

    public scrollBy = (_: unknown, pixels: number) => {
        $(this.scrollableEl).animate({ scrollLeft: "+=" + pixels }, 150);
        if (!this.isRegularTable) {
            $(this.scrollableStickyEl).animate({ scrollLeft: "+=" + pixels }, 150);
        }
    };

    public scrollToColumn = <E extends HTMLElement>(
        columnElement: E | null | undefined,
        behavior: ScrollBehavior = "smooth",
    ): void => {
        const scrollableElement = this.scrollableEl ?? this.scrollableStickyEl;

        if (!scrollableElement || !columnElement) {
            return;
        }

        const currentScrollPosition = scrollableElement.scrollLeft;

        // Calculate the position of the column element relative to the table
        const columnPosition = columnElement.offsetLeft - this.tableEl.offsetLeft;

        // Calculate the rightmost visible position in the scrollable container
        const maxVisiblePosition = currentScrollPosition + scrollableElement.clientWidth;

        // Only scroll if the column is not fully visible
        if (columnPosition < currentScrollPosition || columnPosition > maxVisiblePosition) {
            scrollableElement.scrollTo({
                top: 0,
                left: currentScrollPosition + columnPosition,
                behavior,
            });
        }
    };

    public getColumns() {
        let pinnedWidth: number | string = 0;
        const pinnedSticky: FixmeType[] = [],
            pinned: FixmeType[] = [],
            scrollableSticky: FixmeType[] = [],
            scrollable: FixmeType[] = [],
            { tableData = [], tableColumns, tableMetadata, onSort, tableOptions, getStableKey } = this.props;

        const expandStyle =
            tableOptions.EnrichedRowComponent && tableOptions.shouldApplyEnrichedRowHeightToCell
                ? { height: tableOptions.enrichedRowComponentHeight }
                : null;

        const ColumnComponent = tableOptions.columnComponent ?? FlexTableColumn;

        tableColumns.forEach((col, colIndex, arr) => {
            const pinnedCol = [],
                scrollableCol = [],
                isLast = colIndex === arr.length - 1,
                style = setColumnWidth(true, col, isLast);

            if (col.fixed) {
                // @ts-ignore FIXME: strict migration - col.width typed as optional but always set for fixed columns at runtime
                pinnedWidth = isLast ? "100%" : Number(pinnedWidth) + col.width;

                if (!this.isRegularTable) {
                    pinnedSticky.push(
                        <FlexStickyTableHeaderCell
                            key={`${getStableKey(colIndex, col)}_header`}
                            index={colIndex}
                            columnConfig={col}
                            tableMetadata={tableMetadata}
                            isLast={isLast}
                            onSort={onSort}
                            style={style}
                            onDragStart={this.dragStart}
                            colMinWidth={this.COL_MIN_WIDTH}
                            onSelectAllRows={this.props.onSelectAllRows}
                            allRowsSelected={this.props.allRowsSelected}
                        />,
                    );
                } else {
                    pinnedCol.push(
                        <FlexTableHeaderCell
                            key={`${getStableKey(colIndex, col)}_header`}
                            index={colIndex}
                            columnConfig={col}
                            tableMetadata={tableMetadata}
                            onSort={onSort}
                            onSelectAllRows={this.props.onSelectAllRows}
                            allRowsSelected={this.props.allRowsSelected}
                        />,
                    );
                }
                tableData.forEach((row, rowIndex) => {
                    const cellStyle = rowIndex === this.state.clickedRowIndex ? expandStyle : null;
                    const cellKey = `${getStableKey(rowIndex, col, row)}_field_${col.field}`;
                    pinnedCol.push(
                        <FlexTableCell
                            key={cellKey}
                            cellKey={cellKey}
                            columnConfig={{ ...col }}
                            row={{ ...row }}
                            rowIndex={rowIndex}
                            colIndex={colIndex}
                            parentProps={this.props}
                            style={cellStyle}
                            onCellClick={this.onCellClickEvent}
                            onRowHover={this.onRowHover}
                        />,
                    );
                });
                pinned.push(
                    <ColumnComponent
                        key={`${getStableKey(colIndex, col)}_col`}
                        columnConfig={col}
                        columnCells={pinnedCol}
                        style={style}
                        colMinWidth={this.COL_MIN_WIDTH}
                        onDragStart={this.dragStart}
                    />,
                );
            } else {
                if (!this.isRegularTable) {
                    scrollableSticky.push(
                        <FlexStickyTableHeaderCell
                            key={`${getStableKey(colIndex, col)}_header`}
                            index={colIndex}
                            columnConfig={col}
                            tableMetadata={tableMetadata}
                            isLast={isLast}
                            onSort={onSort}
                            style={style}
                            onDragStart={this.dragStart}
                            colMinWidth={this.COL_MIN_WIDTH}
                        />,
                    );
                } else {
                    scrollableCol.push(
                        <FlexTableHeaderCell
                            key={`${getStableKey(colIndex, col)}_header`}
                            index={colIndex}
                            columnConfig={col}
                            tableMetadata={tableMetadata}
                            onSort={onSort}
                        />,
                    );
                }

                tableData.forEach((row, rowIndex) => {
                    const cellStyle = rowIndex === this.state.clickedRowIndex ? expandStyle : null;
                    const clickableCls =
                        tableOptions.EnrichedRowComponent && tableOptions.shouldEnrichRow?.(this.props, rowIndex)
                            ? " clickable"
                            : "";
                    const cellKey = `${getStableKey(rowIndex, col, row)}_field_${col.field}`;
                    scrollableCol.push(
                        <FlexTableCell
                            key={cellKey}
                            cellKey={cellKey}
                            columnConfig={{ ...col, cellClass: col.cellClass + clickableCls }}
                            row={row}
                            rowIndex={rowIndex}
                            colIndex={colIndex}
                            parentProps={this.props}
                            style={cellStyle}
                            onCellClick={this.onCellClickEvent}
                            onRowHover={this.onRowHover}
                        />,
                    );
                });
                scrollable.push(
                    <ColumnComponent
                        key={`${getStableKey(colIndex, col)}_col`}
                        columnConfig={col}
                        columnCells={scrollableCol}
                        style={style}
                        colMinWidth={this.COL_MIN_WIDTH}
                        isLast={isLast}
                        onDragStart={this.dragStart}
                    />,
                );
            }
        });

        return {
            pinnedWidth,
            pinnedSticky,
            pinned,
            scrollableSticky,
            scrollable,
        };
    }

    public dragStart = (col: FixmeType, e: MouseEvent<HTMLElement>) => {
        const el = $(e.currentTarget),
            tableEl = $(this.tableEl),
            body = document.body;

        this.dragged = {
            x: {
                start: e.pageX,
                // @ts-ignore FIXME: strict migration - jQuery .offset() typed as possibly undefined, but element exists at runtime
                min: el.parent().offset().left + this.COL_MIN_WIDTH,
                // @ts-ignore FIXME: strict migration - jQuery .offset()/.width() typed as possibly undefined, but element exists at runtime
                max: tableEl.offset().left + tableEl.width(),
                currentConstrained: e.pageX,
            },
            // @ts-ignore FIXME: strict migration - jQuery .offset() typed as possibly undefined, but element exists at runtime
            y: el.offset().top,
            el: $(document.createElement("div")).attr("class", "resizeable-border-ghost"),
            anchorEl: $(document.createElement("div")).attr("class", "resizeable-anchor-border"),
            column: col,
            originalWidth: el.parent().width(),
        };
        tableEl.append(this.dragged.el);
        el.parent().prepend(this.dragged.anchorEl);
        this.dragged.el.offset({ top: this.dragged.y, left: this.dragged.x.start });
        body.addEventListener("mousemove", this.dragging, this.passiveEventOptions);
        body.addEventListener("mouseup", this.dragEnd, this.passiveEventOptions);
    };

    public dragging = (e: globalThis.MouseEvent) => {
        if (this.dragged.el) {
            this.dragged.x.currentConstrained =
                e.pageX < this.dragged.x.min
                    ? this.dragged.x.min
                    : e.pageX > this.dragged.x.max
                      ? this.dragged.x.max
                      : e.pageX;
            this.dragged.el.offset({
                top: this.dragged.y,
                left: this.dragged.x.currentConstrained,
            });
        }
    };

    public dragEnd = (): void => {
        if (this.dragged.el) {
            const dragged = this.dragged,
                diff = dragged.x.currentConstrained - dragged.x.start,
                width = dragged.originalWidth + diff,
                body: any = document.body;

            dragged.el.remove();
            dragged.anchorEl.remove();
            this.props.onColumnResize?.(dragged.column, width);
            this.dragged = {};
            body.removeEventListener("mousemove", this.dragging, {
                ...this.passiveEventOptions,
                capture: true,
            });
            body.removeEventListener("mouseup", this.dragEnd, {
                ...this.passiveEventOptions,
                capture: true,
            });
        }
        window.dispatchEvent(this.columnsResizeEvent);
    };

    public onRowHover = (e: MouseEvent<HTMLElement>) => {
        if (this.props.disableRowHover) {
            return;
        }

        if (!this.dragged.el) {
            // do not highlight row when resizing/dragging column
            const targetEl = e.currentTarget;
            const hoveredRowIndex = $(targetEl).index();

            if (this.activeRowEl) {
                this.activeRowEl.removeClass("active-row active-actions-row");
            }
            this.setActiveRowElement(hoveredRowIndex + 1);
            this.props.onRowHover?.(e);

            if (this.props.tableOptions.rowActionsComponents) {
                const tableRowActionsEl = this.tableWrapperEl.querySelector<HTMLElement>(".tableRowActions");
                if (this.rowActionsHeight === -1) {
                    // set rowActions' height & background for the first time
                    const gray = colorsPalettes.carbon[25];
                    const white = colorsPalettes.carbon[0];
                    const actionsWidth = this.rowActionBgWidth * this.props.tableOptions.rowActionsComponents.length;
                    const background1 = `linear-gradient(to left, ${gray} 0px, ${gray} ${
                        actionsWidth + 20
                    }px, transparent ${actionsWidth + 50}px, transparent 100%)`;
                    const background2 = `linear-gradient(to left, ${white} 0px, ${white} ${
                        actionsWidth + 20
                    }px, transparent ${actionsWidth + 50}px, transparent 100%)`;
                    const floatingRowActionsEl =
                        this.tableWrapperEl.querySelector<HTMLElement>(".tableRowActions--floating");
                    this.rowActionsHeight = targetEl.offsetHeight;
                    // @ts-ignore FIXME: strict migration - tableRowActionsEl/floatingRowActionsEl possibly null; markup is rendered by getRowActionsMarkup when rowActionsComponents is set, so elements exist at runtime here
                    tableRowActionsEl.style.height = this.rowActionsHeight + "px";
                    // @ts-ignore FIXME: strict migration - see above
                    floatingRowActionsEl.style.height = this.rowActionsHeight + "px";
                    // @ts-ignore FIXME: strict migration - see above
                    tableRowActionsEl.style.background = background1;
                    // @ts-ignore FIXME: strict migration - see above
                    floatingRowActionsEl.style.background = background2;
                }
                this.rowActionsIndex = hoveredRowIndex;
                // @ts-ignore FIXME: strict migration - tableRowActionsEl possibly null; rendered when rowActionsComponents set
                tableRowActionsEl.style.top = this.getTableRowActionTop(targetEl) + "px";
                this.activeRowEl && this.activeRowEl.addClass("active-actions-row");
                // @ts-ignore FIXME: strict migration - see above
                tableRowActionsEl.style.opacity = "1";
            } else {
                this.activeRowEl && this.activeRowEl.addClass("active-row");
            }
        }
    };

    public setActiveRowElement = (row: number) => {
        this.activeRowEl = $(this.tableEl).find(`.swReactTableCell:nth-child(${row})`);
        this.setState({ activeRowIndex: row });
    };

    public getTableRowActionTop = (cellOnHover: FixmeType) => {
        // @ts-ignore FIXME: strict migration - jQuery .offset() typed as possibly undefined, but element exists at runtime
        return $(cellOnHover).offset().top - $(cellOnHover).closest(".flex-table").offset().top;
    };

    public setSelectedRowElement = (row: number) => {
        this.selectedRowEl = $(this.tableEl).find(`.swReactTableCell:nth-child(${row})`);
        this.selectedRowEl && this.selectedRowEl.addClass("selected");
    };

    public removeSelectedRowElement = () => {
        this.selectedRowEl && this.selectedRowEl.removeClass("selected");
    };

    public toggleSelectedRowElement = (clickedRowIndex: number) => {
        if (this.prevSelectedRowIndex && clickedRowIndex === this.prevSelectedRowIndex) {
            if (this.selectedRowEl && this.selectedRowEl.hasClass("selected")) {
                this.removeSelectedRowElement();
            } else {
                this.selectedRowEl && this.selectedRowEl.addClass("selected");
            }
        } else {
            this.removeSelectedRowElement();
            this.setSelectedRowElement(clickedRowIndex);
        }
        this.prevSelectedRowIndex = clickedRowIndex;
    };

    public onTableWrapperMouseLeave = () => {
        $(this.tableWrapperEl).find(".swReactTableCell").removeClass("active-row active-actions-row");

        if (this.props.tableOptions.rowActionsComponents) {
            // do not highlight row when resizing/dragging column
            const tableRowActionsEl = this.tableWrapperEl.querySelector<HTMLElement>(".tableRowActions");
            this.rowActionsIndex = -1;
            // @ts-ignore FIXME: strict migration - tableRowActionsEl possibly null; rendered when rowActionsComponents set
            tableRowActionsEl.style.opacity = "0";
            // @ts-ignore FIXME: strict migration - see above
            tableRowActionsEl.style.top = "-1000px";
            this.setState({ activeRowIndex: -1 });
            // this.activeRowEl && this.activeRowEl.removeClass('active-row active-actions-row');
        }

        this.props.onMouseTableLeave?.();
    };

    private collapseEnrichedRow = (rowIndex: number): void => {
        this.removeClickedRowClass();

        this.setState({
            clickedRowIndex: -1,
        });

        this.props.tableOptions.onEnrichedRowClick?.(
            false,
            rowIndex,
            this.props.tableData[rowIndex] as unknown as string,
        );
    };

    private collapseEnrichedRowAndCloseDropdown = (rowIndex: number): void => {
        this.enrichedRowDropdown?.popupClickRef?.closePopup();
        this.collapseEnrichedRow(rowIndex);
    };

    private handleEnrichedRowOutsideClick = (currentRowIndex: number): void => {
        const isEnrichedRowDropdownOpen = this.enrichedRowDropdown?.popupClickRef?.state.isOpen;

        if (!isEnrichedRowDropdownOpen) {
            return;
        }

        this.collapseEnrichedRowAndCloseDropdown(currentRowIndex);
    };

    public onCellClickEvent =
        (columnConfig: FlexTableColumnConfig) =>
        (e: MouseEvent, index?: number, isEnrichedRowTriggerClick = false) => {
            const clickedRowIndex = index ?? $(e.currentTarget).index();
            const { tableOptions, tableData = [] } = this.props;

            if (tableOptions.onCellClick) {
                tableOptions.onCellClick(e, clickedRowIndex, tableData[clickedRowIndex], columnConfig);
                if (tableOptions.activeSelectedRow) {
                    this.toggleSelectedRowElement(clickedRowIndex + 1);
                }
            }

            if (tableOptions.EnrichedRowComponent) {
                try {
                    // not sure how visibleParents are built, thus try catch.
                    // @ts-ignore FIXME: strict migration - visibleParents typed as unknown; safely indexed inside try/catch by design
                    if (this.props.visibleParents[clickedRowIndex]?.expanded) {
                        this.props.toggleChildRows(tableData[clickedRowIndex], "");
                    }
                } catch (e) {}

                const isEnrichedRowHiddenOnOutsideClick =
                    this.props.tableOptions.enrichedRowOptions?.isHiddenOnOutsideClick ?? true;

                if (
                    tableOptions.shouldEnrichRow &&
                    tableOptions.shouldEnrichRow(this.props, clickedRowIndex, e, isEnrichedRowTriggerClick)
                ) {
                    if (clickedRowIndex === this.state.clickedRowIndex) {
                        if (isEnrichedRowHiddenOnOutsideClick) {
                            this.collapseEnrichedRow(clickedRowIndex);
                        } else {
                            this.collapseEnrichedRowAndCloseDropdown(clickedRowIndex);
                        }
                    } else {
                        if (isEnrichedRowTriggerClick && !isEnrichedRowHiddenOnOutsideClick) {
                            // hide the current enriched row, if clicked on a different enriched row trigger
                            this.handleEnrichedRowOutsideClick(this.state.clickedRowIndex);
                        }
                        // change the dropdown's button position according to current clicked row
                        this.removeClickedRowClass();
                        this.activeRowEl?.addClass("active-row-clicked");
                        this.setState(
                            {
                                clickedRowIndex,
                            },
                            () => {
                                setTimeout(() => {
                                    // timeout to allow height animation to finish
                                    const clickedCell = this.tableEl.querySelector<HTMLElement>(
                                        `.swReactTableCell:nth-child(${this.state.clickedRowIndex + 1})`,
                                    );
                                    const dropdownParent = this.rowDropdownEl.parentNode as HTMLElement;
                                    dropdownParent.style.position = "absolute";
                                    const offsetOrigin = dropdownParent.offsetParent as HTMLElement | null;
                                    if (
                                        this.props.tableOptions.syncEnrichedRowHeightToContent &&
                                        clickedCell &&
                                        offsetOrigin
                                    ) {
                                        // Position the dropdown at the exact top of the clicked row using real
                                        // geometry. Reconstructing it from headerHeight + offsetTop misses the
                                        // above-header bar (e.g. selection toolbar), pushing the dropdown up.
                                        dropdownParent.style.top = `${
                                            clickedCell.getBoundingClientRect().top -
                                            offsetOrigin.getBoundingClientRect().top
                                        }px`;
                                    } else {
                                        dropdownParent.style.top =
                                            4 + this.headerHeight + (clickedCell?.offsetTop ?? 0) + "px";
                                    }
                                    this.rowDropdownEl.click();
                                    this.syncEnrichedRowHeightToContent();
                                }, this.props.tableOptions.enrichedRowComponentTimeout || 500);
                            },
                        );
                        tableOptions.onEnrichedRowClick?.(
                            true,
                            clickedRowIndex,
                            // TODO update onEnrichedRowClick type
                            tableData[clickedRowIndex] as unknown as string,
                        );
                    }
                } else {
                    if (isEnrichedRowHiddenOnOutsideClick) {
                        this.collapseEnrichedRow(clickedRowIndex);
                    } else {
                        this.collapseEnrichedRowAndCloseDropdown(clickedRowIndex);
                    }
                }
            }
        };

    public onDropdownToggle = (_isOpen: boolean, isOutsideClick: boolean, event?: MouseEvent): void => {
        const shouldHideEnrichedRowOnOutsideClick =
            isOutsideClick && event && !$(event.target).closest(".swReactTableCell").length;

        if (shouldHideEnrichedRowOnOutsideClick) {
            this.collapseEnrichedRow(this.state.clickedRowIndex);
        }
    };

    public removeClickedRowClass = () => {
        this.disconnectEnrichedRowObserver();
        $(this.tableEl)
            .find(`.swReactTableCell:nth-child(${this.state.clickedRowIndex + 1})`)
            .removeClass("active-row-clicked")
            .css("height", "");
    };

    private getEnrichedRowPopupEl = (): HTMLElement | null => {
        const appendTo = this.props.tableOptions.enrichedRowComponentAppendTo;
        const root = (appendTo && document.querySelector<HTMLElement>(appendTo)) || this.tableWrapperEl;
        return root?.querySelector<HTMLElement>(".DropdownContent-container") ?? null;
    };

    private disconnectEnrichedRowObserver = (): void => {
        this.enrichedRowResizeObserver?.disconnect();
        this.enrichedRowResizeObserver = undefined;
    };

    // Keeps the in-flow space reserved by the expanded row equal to the floating dropdown's
    // actual height. The dropdown content loads asynchronously, so a ResizeObserver re-applies
    // the height whenever the content grows/shrinks, preventing overlap (tall content) and
    // empty gaps (short content) below the nested table.
    private syncEnrichedRowHeightToContent = (attempt = 0): void => {
        if (!this.props.tableOptions.syncEnrichedRowHeightToContent) {
            return;
        }
        const popup = this.getEnrichedRowPopupEl();
        if (!popup) {
            if (attempt < 10) {
                requestAnimationFrame(() => this.syncEnrichedRowHeightToContent(attempt + 1));
            }
            return;
        }
        this.disconnectEnrichedRowObserver();
        const applyHeight = () => {
            const height = popup.getBoundingClientRect().height;
            if (height <= 0) {
                return;
            }
            $(this.tableEl)
                .find(`.swReactTableCell:nth-child(${this.state.clickedRowIndex + 1})`)
                .css("height", `${Math.ceil(height)}px`);
        };
        applyHeight();
        this.enrichedRowResizeObserver = new ResizeObserver(applyHeight);
        this.enrichedRowResizeObserver.observe(popup);
    };

    public toggleRowAppearance = (isHidden: boolean): Promise<VoidFunction> => {
        return new Promise((resolve) => {
            const activeRowEl = this.activeRowEl;
            if (isHidden) {
                activeRowEl.addClass("swReactTableCell--disappear");
                activeRowEl.one("transitionend", () => {
                    activeRowEl.hide();
                    resolve(() => activeRowEl.removeClass("swReactTableCell--disappear").show());
                });
            }
        });
    };

    public onHorizontalScroll = (e: Event) => {
        const scrollableElem = e.currentTarget as HTMLElement;
        $(this.scrollableStickyEl).scrollLeft(scrollableElem.scrollLeft);
        $(".swTable-scroll").removeClass("end-of-scroll");
        if (scrollableElem.scrollLeft !== 0) {
            $(".swReactTable-pinned").addClass("shadow-delimiter");
        }
        if (scrollableElem.scrollLeft === 0) {
            $(".swTable-scroll--left").addClass("end-of-scroll");
            $(".swReactTable-pinned").removeClass("shadow-delimiter");
        } else if (scrollableElem.scrollLeft === scrollableElem.scrollWidth - scrollableElem.offsetWidth) {
            $(".swTable-scroll--right").addClass("end-of-scroll");
        }
    };

    public onPageResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            // Resize event(s) ended, execute this code only now (Debounce)
            this.checkResponsiveState();
        }, this.onPageResizeDelta);
    };

    public onRowActionClick = () => {
        if (this.props.tableOptions.highlightClickedRow) {
            if (this.rowHighlightIndex !== this.rowActionsIndex) {
                this.clearHighlightClickedRow();
                this.highlightClickedRow(this.rowActionsIndex);
            } else {
                this.clickedRowEl.toggleClass("highlight-clicked-row");
            }
        }

        this.lastValidRowActionsIndex = this.rowActionsIndex;
    };

    public clearHighlightClickedRow = () => {
        this.clickedRowEl && this.clickedRowEl.removeClass("highlight-clicked-row");
    };

    public updateHighlightClickedRow = () => {
        if (this.clickedRowEl) {
            const index = this.clickedRowEl.index();
            this.highlightClickedRow(index);
        }
    };

    public highlightClickedRow = (index: number) => {
        this.rowHighlightIndex = index;
        this.clickedRowEl = $(this.tableEl).find(`.swReactTableCell:nth-child(${this.rowHighlightIndex + 1})`);
        this.clickedRowEl.addClass("highlight-clicked-row");
    };

    public toggleFloatingActionsRow = () => {
        const floatingRowActionsEl = this.tableWrapperEl.querySelector<HTMLElement>(".tableRowActions--floating");
        if (this.floatingActionsRowIndex > -1) {
            // @ts-ignore FIXME: strict migration - floatingRowActionsEl/.tableRowActions possibly null; rendered when rowActionsComponents set
            floatingRowActionsEl.style.top =
                // @ts-ignore FIXME: strict migration - jQuery overload + .position() on JQueryStatic; legacy jQuery typing limitation
                $(this.tableWrapperEl.querySelector(".tableRowActions")).position().top + "px";
            // @ts-ignore FIXME: strict migration - floatingRowActionsEl possibly null; see above
            floatingRowActionsEl.style.opacity = "1";
        } else {
            // @ts-ignore FIXME: strict migration - floatingRowActionsEl possibly null; see above
            floatingRowActionsEl.style.top = -1000 + "px";
            // @ts-ignore FIXME: strict migration - floatingRowActionsEl possibly null; see above
            floatingRowActionsEl.style.opacity = "0";
        }
    };

    public getTableClasses = () => {
        return classNames(
            "flex-table",
            {
                responsive: this.state.isResponsive,
                "flex-table--expandable":
                    this.props.tableOptions.EnrichedRowComponent &&
                    (this.props.tableOptions.enrichedRowComponentHeight ?? 0) > 0,
            },
            this.props.className,
        );
    };

    public getRowActionsMarkup = (isSelectionRow: boolean) => {
        const { tableOptions, tableData = [], tableMetadata } = this.props;
        return (
            tableOptions.rowActionsComponents && (
                <div
                    className={classNames("tableRowActions", {
                        "tableRowActions--floating": isSelectionRow,
                    })}
                    onClickCapture={this.onRowActionClick}
                >
                    {(tableOptions.rowActionsComponents as unknown as any[]).map((component, idx) => {
                        const injectProps = {
                            rowIndex: this.state.activeRowIndex,
                            row: tableData[this.state.activeRowIndex - 1],
                            onClick: (props: FixmeType) => {
                                return component.props.onClick(
                                    props,
                                    tableData[this.lastValidRowActionsIndex],
                                    tableMetadata.totalCount,
                                    this.activeRowEl,
                                    this.exposable,
                                );
                            },
                            onToggle: (isOpen: boolean, isOutsideClick: boolean) => {
                                // only in cases where the action Component is a dropdown
                                component.props.onToggle(isOpen);
                                if (!isOpen || isOutsideClick) {
                                    this.floatingActionsRowIndex = -1;
                                    this.toggleFloatingActionsRow();
                                } else if (isOpen) {
                                    this.floatingActionsRowIndex = this.rowActionsIndex;
                                    this.toggleFloatingActionsRow();
                                }
                            },
                        };
                        if (typeof component === "function") {
                            return component(injectProps);
                        }
                        return {
                            ...component,
                            key: `ra${idx}`,
                            props: {
                                ...component.props,
                                ...injectProps,
                            },
                        };
                    })}
                </div>
            )
        );
    };
}
